import { supabase } from '@/lib/supabase';

interface Language {
  id: string;
  country_code: string;
  language_code: string;
  culture_code: string;
  name: string | null;
}

class LanguageCacheService {
  private cache: Language[] | null = null;
  private isLoading = false;
  private loadPromise: Promise<Language[]> | null = null;

  async getLanguages(): Promise<Language[]> {
    if (this.cache) {
      console.log('[LanguageCache] Returning cached languages:', this.cache.length);
      return this.cache;
    }

    if (this.isLoading && this.loadPromise) {
      console.log('[LanguageCache] Already loading, waiting for existing promise');
      return this.loadPromise;
    }

    this.isLoading = true;
    this.loadPromise = this.loadLanguagesFromDatabase();

    try {
      const languages = await this.loadPromise;
      this.cache = languages;
      console.log('[LanguageCache] Successfully loaded and cached languages:', languages.length);
      return languages;
    } finally {
      this.isLoading = false;
      this.loadPromise = null;
    }
  }

  private async loadLanguagesFromDatabase(): Promise<Language[]> {
    console.log('[LanguageCache] Loading languages from database...');
    const startTime = Date.now();

    const { data, error } = await supabase
      .from('languages_for_translations')
      .select('id, country_code, language_code, culture_code, name')
      .order('name');

    if (error) {
      console.error('[LanguageCache] Failed to load languages:', error);
      throw error;
    }

    const duration = Date.now() - startTime;
    console.log(`[LanguageCache] Loaded ${data?.length || 0} languages in ${duration}ms`);

    return data || [];
  }

  getCachedLanguages(): Language[] | null {
    return this.cache;
  }

  clearCache(): void {
    console.log('[LanguageCache] Clearing language cache');
    this.cache = null;
  }

  async initializeCache(): Promise<void> {
    console.log('[LanguageCache] Initializing cache at app start...');
    await this.getLanguages();
  }
}

export const languageCacheService = new LanguageCacheService();
export type { Language };
